import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import {
  Sparkles, ExternalLink, Edit, Trash2, Loader2, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import TabPageLayout, { PageLoader } from './TabPageLayout';
import ServiceToggle from './ServiceToggle';
import apiV2 from '../utils/apiV2';
import ReactMarkdown from 'react-markdown';

// Loading overlay component with typewriter-style animation
const GeneratingOverlay = () => {
  const [dots, setDots] = useState('');
  const messages = [
    'Analyzing your business',
    'Researching SEO keywords',
    'Writing engaging content',
    'Optimizing for search engines',
    'Finalizing your blog'
  ];
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const dotInterval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 400);

    const messageInterval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % messages.length);
    }, 3000);

    return () => {
      clearInterval(dotInterval);
      clearInterval(messageInterval);
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-white/95 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="blog-generating-overlay">
      <div className="text-center max-w-md px-6">
        <div className="w-16 h-16 mx-auto mb-6 relative">
          <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
          <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin"></div>
          <Sparkles className="w-6 h-6 text-blue-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>

        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {messages[messageIndex]}{dots}
        </h2>

        <p className="text-gray-500 text-sm mb-6">
          Our AI is crafting a unique, SEO-optimized blog for your business
        </p>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-amber-800 text-sm font-medium">Please stay on this page</p>
          <p className="text-amber-700 text-xs mt-1">This usually takes 30-60 seconds</p>
        </div>
      </div>
    </div>
  );
};

// Labeled group of keyword chips (keywords / search phrases / competitor terms)
const KeywordGroup = ({ label, hint, items, tone = 'blue', testId }) => {
  if (!items || items.length === 0) return null;
  const tones = {
    blue: 'bg-blue-50 text-blue-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    gray: 'bg-gray-100 text-gray-600',
  };
  return (
    <div data-testid={testId}>
      <p className="field-label mb-0.5">{label}</p>
      {hint && <p className="field-hint mt-0 mb-2">{hint}</p>}
      <div className="flex flex-wrap gap-1.5">
        {items.map((kw, i) => (
          <span key={i} className={`px-2.5 py-1 ${tones[tone]} text-xs rounded-md`}>{kw}</span>
        ))}
      </div>
    </div>
  );
};

const BlogTab = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [companyDescription, setCompanyDescription] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState({});
  const [fullBlog, setFullBlog] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const orgId = localStorage.getItem('organizationId');
  const orgSlug = localStorage.getItem('organizationSlug');

  const { data, error, isLoading, mutate } = useSWR(
    orgId ? `/blogs/organizations/${orgId}/blogs` : null,
    (url) => apiV2.get(url).then(res => res.data),
    { revalidateOnFocus: true }
  );

  // Org profile — the single source for the company website (Settings → Profile)
  const { data: org } = useSWR(
    '/auth/organizations/me',
    (url) => apiV2.get(url).then(res => res.data),
    { revalidateOnFocus: false, dedupingInterval: 30000 }
  );
  const companyWebsite = org?.companyWebsite || '';

  const blogs = data?.blogs || [];
  const latestBlogMeta = blogs[0];

  useEffect(() => {
    const fetchFullBlog = async () => {
      if (latestBlogMeta?.id) {
        try {
          const response = await apiV2.get(`/blogs/organizations/${orgId}/blogs/${latestBlogMeta.id}`);
          setFullBlog(response.data);
        } catch (err) {
          // Silent fail - error already handled by SWR
        }
      } else {
        setFullBlog(null);
      }
    };
    fetchFullBlog();
  }, [latestBlogMeta?.id, orgId]);

  const handleGenerate = async () => {
    if (!companyDescription || companyDescription.length < 20) {
      toast.error('Please provide at least 2-3 sentences about your company');
      return;
    }

    setIsGenerating(true);
    try {
      await apiV2.post(`/blogs/organizations/${orgId}/blogs/generate`, {
        company_description: companyDescription,
        website_url: companyWebsite || null
      });
      toast.success('Blog generated successfully!');
      mutate();
      setCompanyDescription('');
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const msg = errorDetail.map(e => e.msg || e.message).join(', ');
        toast.error(msg || 'Validation error');
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else if (typeof errorDetail === 'object' && errorDetail?.msg) {
        toast.error(errorDetail.msg);
      } else {
        toast.error('Failed to generate blog');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePublish = async (blogId, publish) => {
    try {
      await apiV2.patch(`/blogs/organizations/${orgId}/blogs/${blogId}`, {
        status: publish ? 'published' : 'draft'
      });
      toast.success(publish ? 'Blog published!' : 'Blog unpublished');
      mutate();
      if (fullBlog) setFullBlog({ ...fullBlog, status: publish ? 'published' : 'draft', published_at: publish ? new Date().toISOString() : null });
    } catch (err) {
      toast.error('Failed to update blog status');
    }
  };

  const handleUpdate = async (blogId, content) => {
    try {
      await apiV2.patch(`/blogs/organizations/${orgId}/blogs/${blogId}`, content);
      toast.success('Blog updated!');
      mutate();
      setEditMode(false);
      setFullBlog({ ...fullBlog, ...content });
    } catch (err) {
      toast.error('Failed to update blog');
    }
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;
    try {
      await apiV2.delete(`/blogs/organizations/${orgId}/blogs/${blogId}`);
      toast.success('Blog deleted');
      mutate();
      setFullBlog(null);
    } catch (err) {
      toast.error('Failed to delete blog');
    }
  };

  if (isLoading) {
    return (
      <TabPageLayout title="SEO" subtitle="AI-powered blog content for search visibility">
        <PageLoader />
      </TabPageLayout>
    );
  }

  if (error) {
    return (
      <TabPageLayout title="SEO" subtitle="AI-powered blog content for search visibility">
        <div className="max-w-xl card-soft p-6 text-center">
          <p className="text-gray-600 mb-4">Failed to load blog data</p>
          <Button onClick={() => mutate()} className="bg-blue-600 hover:bg-blue-700 text-white h-10">
            Try Again
          </Button>
        </div>
      </TabPageLayout>
    );
  }

  const seo = fullBlog?.seo_meta || {};

  return (
    <TabPageLayout
      title="SEO"
      subtitle="AI-powered blog content for search visibility"
    >
      {isGenerating && <GeneratingOverlay />}

      <div className="max-w-xl space-y-5">

        {/* No Blog - Generator */}
        {!latestBlogMeta && (
          <div className="card-soft p-5 sm:p-6" data-testid="blog-generator-card">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="settings-section-title">Generate an SEO blog</h3>
                <p className="settings-section-subtitle">
                  We write a unique, search-optimized post tailored to your business, services, and location.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="field-label">
                  About your company <span className="text-red-500">*</span>
                </label>
                <Textarea
                  placeholder="What you offer, who it's for, your location, and what makes you unique…"
                  value={companyDescription}
                  onChange={(e) => setCompanyDescription(e.target.value)}
                  className="min-h-[100px] resize-none mt-1"
                  maxLength={500}
                  data-testid="blog-company-description"
                />
                <p className="field-hint">{companyDescription.length}/500 · at least 20 characters</p>
              </div>

              <div>
                <label className="field-label">Website</label>
                <Input
                  type="url"
                  value={companyWebsite}
                  readOnly
                  disabled
                  placeholder="No website set"
                  className="mt-1 bg-gray-50 text-gray-500 cursor-not-allowed"
                  data-testid="blog-website-url"
                />
                <p className="field-hint">
                  Comes from{' '}
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new Event('openSettings'))}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                    data-testid="blog-website-settings-link"
                  >
                    Settings → Profile
                  </button>
                </p>
              </div>

              <Button
                onClick={handleGenerate}
                disabled={isGenerating || companyDescription.length < 20}
                className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="blog-generate-btn"
              >
                {isGenerating ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />Generating…</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Generate Blog</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Existing Blog */}
        {latestBlogMeta && fullBlog && (
          <>
            {/* Controls */}
            <div className="card-soft p-4" data-testid="blog-controls">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700">Publish</span>
                  <span data-testid="blog-publish-toggle">
                    <ServiceToggle
                      checked={fullBlog.status === 'published'}
                      onChange={(checked) => handlePublish(fullBlog.id, checked)}
                    />
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${fullBlog.status === 'published' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {fullBlog.status === 'published' ? 'Live' : 'Draft'}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9"
                    data-testid="blog-edit-btn"
                    onClick={() => {
                      setEditMode(!editMode);
                      if (!editMode) {
                        setEditedContent({
                          title: fullBlog.title,
                          content: fullBlog.content,
                          website_url: fullBlog.website_url || ''
                        });
                      }
                    }}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(fullBlog.id)}
                    className="h-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="blog-delete-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Edit Mode */}
            {editMode && (
              <div className="card-soft p-5 sm:p-6 space-y-4" data-testid="blog-edit-card">
                <h3 className="settings-section-title">Edit blog</h3>
                <div>
                  <label className="field-label">Title</label>
                  <Input
                    value={editedContent.title || ''}
                    onChange={(e) => setEditedContent({ ...editedContent, title: e.target.value })}
                    className="mt-1"
                    data-testid="blog-edit-title"
                  />
                </div>
                <div>
                  <label className="field-label">Content</label>
                  <Textarea
                    value={editedContent.content || ''}
                    onChange={(e) => setEditedContent({ ...editedContent, content: e.target.value })}
                    className="min-h-[300px] font-mono text-sm mt-1"
                    data-testid="blog-edit-content"
                  />
                </div>
                <div>
                  <label className="field-label">Website link</label>
                  <Input
                    type="url"
                    value={companyWebsite}
                    readOnly
                    disabled
                    placeholder="No website set"
                    className="mt-1 bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <p className="field-hint">
                    Comes from{' '}
                    <button
                      type="button"
                      onClick={() => window.dispatchEvent(new Event('openSettings'))}
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Settings → Profile
                    </button>
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setEditMode(false)} className="h-10">Cancel</Button>
                  <Button
                    onClick={() => handleUpdate(fullBlog.id, editedContent)}
                    className="h-10 bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="blog-edit-save"
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}

            {/* Preview */}
            {!editMode && (
              <div className="card-soft overflow-hidden" data-testid="blog-preview-card">
                <div className="p-5 sm:p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-2" data-testid="blog-preview-title">{fullBlog.title}</h2>

                  <div className={`prose prose-sm prose-gray max-w-none ${!isExpanded ? 'line-clamp-4' : ''}`}>
                    {isExpanded ? (
                      <ReactMarkdown
                        components={{
                          h2: ({ children }) => <h2 className="text-base font-semibold text-gray-900 mt-4 mb-2">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-sm font-semibold text-gray-800 mt-3 mb-1">{children}</h3>,
                          p: ({ children }) => <p className="text-sm text-gray-600 mb-2">{children}</p>,
                          strong: ({ children }) => <strong className="font-semibold text-gray-800">{children}</strong>,
                          ul: ({ children }) => <ul className="list-disc list-inside text-sm text-gray-600 mb-2">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-inside text-sm text-gray-600 mb-2">{children}</ol>,
                          li: ({ children }) => <li className="mb-1">{children}</li>,
                        }}
                      >
                        {fullBlog.content}
                      </ReactMarkdown>
                    ) : (
                      <p className="text-sm text-gray-600">{fullBlog.excerpt || fullBlog.content?.slice(0, 250)}…</p>
                    )}
                  </div>

                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-3 font-medium"
                    data-testid="toggle-content-btn"
                  >
                    {isExpanded ? (<>See less <ChevronUp className="w-4 h-4" /></>) : (<>See more <ChevronDown className="w-4 h-4" /></>)}
                  </button>

                  {/* SEO keyword intelligence */}
                  {(seo.keywords?.length > 0 || seo.search_phrases?.length > 0 || seo.competitor_keywords?.length > 0) && (
                    <div className="mt-5 pt-5 border-t border-gray-100 space-y-4" data-testid="blog-seo-data">
                      <KeywordGroup
                        label="Primary keywords"
                        hint="What this post is optimized to rank for"
                        items={(seo.keywords || []).slice(0, 8)}
                        tone="blue"
                        testId="blog-keywords"
                      />
                      <KeywordGroup
                        label="Customer search phrases"
                        hint="Real searches your customers type on Google"
                        items={(seo.search_phrases || []).slice(0, 12)}
                        tone="emerald"
                        testId="blog-search-phrases"
                      />
                      <KeywordGroup
                        label="Competitor & category terms"
                        hint="Broader terms you compete for in your area"
                        items={(seo.competitor_keywords || []).slice(0, 8)}
                        tone="gray"
                        testId="blog-competitor-keywords"
                      />
                    </div>
                  )}

                  <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
                    {companyWebsite ? (
                      <a
                        href={companyWebsite.startsWith('http') ? companyWebsite : `https://${companyWebsite}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        {companyWebsite.replace(/^https?:\/\//, '')}
                      </a>
                    ) : (<span />)}

                    {fullBlog.status === 'published' && (
                      <Link
                        to={`/blog/${orgSlug}`}
                        target="_blank"
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        data-testid="blog-view-public-link"
                      >
                        View Blog
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Loading blog details */}
        {latestBlogMeta && !fullBlog && (
          <PageLoader minHeight="h-32" />
        )}
      </div>
    </TabPageLayout>
  );
};

export default BlogTab;
